import styled from 'styled-components';
import { Dialog, Icon } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import {
  closeQuestionsDialog,
  openVinDialog,
  updateVehicleSections,
  UploadType,
  useOpenedQuestionsDialog
} from '../../../services/listerUploadVehicle/v2';
import { Close } from '@material-ui/icons';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useMemo, useState } from 'react';
import { SubmitButton } from './components/SubmitButton';
import { EnterFullScreen, ExitFullScreen } from '../../../components/Icons/FullScreen';
import { useGetVehicleByIdQuery } from '../../../services/api/upload-vehicle';
import { Level3Section } from '../Level3Section';
import { Level2Labels } from '../config';
import { CameraViewContextProvider, useCameraViewContext } from '../CameraView/context';
import { CameraView } from '../CameraView';
import { useGetQuestionsQuery } from 'services/api/vehicle-sections';
import { useHistory } from 'react-router-dom';
import { useInfoToastEffect } from 'components/InfoToast/useInfoToast';
import { SyncManager } from 'db';
import { useUpdateFormAfterSync } from '../hooks/useUpdateFormAfterSync';
import toast from 'react-hot-toast';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  justify-content: space-between;
  padding: 24px;
`;

const StyledDialog = styled(Dialog)<{ isFullScreenMode: boolean }>`
  .vehicle-questions {
    border-radius: 20px;
    background: #fff;
    margin: ${props => (props.isFullScreenMode ? '18px' : '110px 18px')};
    margin: 18px;
    height: ${props => (props.isFullScreenMode ? 'calc(100vh - 36px)' : 'calc(100vh - 220px)')};
    width: 100%;
    transition: height 0.5s cubic-bezier(0.4, 0, 0.2, 1) !important;
    max-height: none;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 500;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const UploadQuestionsDialog = () => {
  const dispatch = useDispatch();

  const { vehicleId, isOpened, type } = useOpenedQuestionsDialog();

  const [isFullScreenMode, setIsFullScreenMode] = useState(false);

  const toggleFullScreen = () => setIsFullScreenMode(curr => !curr);

  const close = () => {
    dispatch(closeQuestionsDialog());
  };

  useInfoToastEffect('Reset Odometer', vehicleId);

  return (
    <StyledDialog
      onClose={close}
      onBackdropClick={close}
      open={isOpened}
      isFullScreenMode={isFullScreenMode}
      classes={{ paperScrollPaper: 'vehicle-questions' }}
    >
      {vehicleId && isOpened && type && (
        <VehicleQuestionsDialogContent
          isFullScreenMode={isFullScreenMode}
          toggleFullScreen={toggleFullScreen}
          vehicleId={vehicleId}
          type={type}
        />
      )}
    </StyledDialog>
  );
};

type Props = {
  vehicleId: number;
  toggleFullScreen: () => void;
  isFullScreenMode: boolean;
  type: UploadType;
};

type SubSectionForm = {
  grade: null | 'good' | 'bad' | 'fair';
  images: Array<{ file: File | null; url: string; notes: string }>;
  notes: string;
};

const getDefaultImages = (sectionId: string, subSectionId: string) => {
  const label = Level2Labels[sectionId][subSectionId].pictureLabel;

  return [
    {
      file: null,
      url: '',
      notes: '',
      type: label ? subSectionId : 'user_defined',
      label
    }
  ];
};

const getDefaultSubSectionValue: (sectionId: string, subSectionId: string) => SubSectionForm = (
  sectionId,
  subSectionId
) => ({
  grade: null,
  images: getDefaultImages(sectionId, subSectionId),
  notes: ''
});

const titleIssuesCheckboxes = [
  'Salvage',
  'Frame',
  'Accident',
  'No Title',
  'Lemon Law',
  'Prior Canadian',
  'Clean',
  'TMU',
  'AutoCheck API',
  'AutoCheck Report',
  'Duplicate Application',
  'Repo'
];

const QuestionsWrapper = styled.div<{ isFullScreenMode: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: scroll;
  max-height: ${props => (props.isFullScreenMode ? 'calc(100vh - 180px)' : 'calc(100vh - 360px)')};
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1) !important;
`;

const createCheckboxDefault = (checkboxes: string[]) => {
  return {
    ...getDefaultSubSectionValue('interior_questions', 'title_issues'),
    checkboxes: checkboxes.map(c => ({
      notes: '',
      selected: true,
      label: c
    }))
  };
};

const interiorFormValues = {
  images: new Array(9).map(() => ({
    file: null,
    url: '',
    notes: '',
    label: ''
  })),
  subsections: {
    odb_check_smog_readiness: getDefaultSubSectionValue('interior_questions', 'odb_check_smog_readiness'),
    title_issues: createCheckboxDefault(titleIssuesCheckboxes),
    radio_turns_on: getDefaultSubSectionValue('interior_questions', 'radio_turns_on'),
    interior_vehicle_odor: getDefaultSubSectionValue('interior_questions', 'interior_vehicle_odor'),
    power_equipment_checked: getDefaultSubSectionValue('interior_questions', 'power_equipment_checked'),
    check_engine_light: getDefaultSubSectionValue('interior_questions', 'check_engine_light'),
    abs_light: getDefaultSubSectionValue('interior_questions', 'abs_light'),
    air_bag_light: getDefaultSubSectionValue('interior_questions', 'air_bag_light'),
    any_other_lights_on: getDefaultSubSectionValue('interior_questions', 'any_other_lights_on'),
    windshield: getDefaultSubSectionValue('interior_questions', 'windshield'),
    keys: getDefaultSubSectionValue('interior_questions', 'keys'),
    air_conditioning: getDefaultSubSectionValue('interior_questions', 'air_conditioning'),
    heater: getDefaultSubSectionValue('interior_questions', 'heater'),
    interior_overall: getDefaultSubSectionValue('interior_questions', 'interior_overall')
  }
};

const VehicleQuestionsDialogContent = ({ toggleFullScreen, isFullScreenMode, vehicleId, type }: Props) => {
  const dispatch = useDispatch();

  const { data: questions } = useGetQuestionsQuery(vehicleId);

  const history = useHistory();

  const initialValues = useMemo(() => {
    if (!questions) return { subsections: {}, images: [], label: '' };
    const { images, subsections, label } = questions.interior_questions;
    return {
      images,
      subsections,
      label
    };
  }, [questions]);

  const close = async () => {
    const syncManager = SyncManager.getInstance(vehicleId);
    if (syncManager.isBusy) {
      const toastId = toast.loading('Saving data...');
      await syncManager.waitUntilSynced();
      toast.dismiss(toastId);
    }
    dispatch(closeQuestionsDialog());
  };

  const closeWithRedirect = async () => {
    await close();
    history.push(`/lister/upload-vehicle/v2/${vehicleId}`);
  };

  useGetVehicleByIdQuery(vehicleId);

  if (!questions) return null;

  const onSubmit = async (v: any) => {
    const syncManager = SyncManager.getInstance(vehicleId);
    if (syncManager.isBusy) {
      const toastId = toast.loading('Saving data...');
      await syncManager.waitUntilSynced();
      toast.dismiss(toastId);
    }
    dispatch(updateVehicleSections({ id: vehicleId, data: v }));
    if (type === 'upload') {
      history.push(`/lister/upload-vehicle/v2/${vehicleId}`);
    } else {
      dispatch(openVinDialog({ vehicleId, type }));
    }
    close();
  };

  return (
    <Wrapper>
      <HeaderRow>
        <Title>Interior Questions</Title>
        <Icon onClick={toggleFullScreen} style={{ marginLeft: 'auto' }}>
          {isFullScreenMode ? <ExitFullScreen /> : <EnterFullScreen />}
        </Icon>
        <Icon onClick={closeWithRedirect}>
          <Close />
        </Icon>
      </HeaderRow>
      <Form
        subscription={{ submitting: true, submitFailed: true, dirty: true }}
        mutators={{
          ...arrayMutators
        }}
        initialValues={{ interior_questions: initialValues, vehicleId }}
        onSubmit={onSubmit}
      >
        {props => (
          <StyledForm onSubmit={props.handleSubmit}>
            <CameraViewContextProvider>
              <RenderForm isFullScreenMode={isFullScreenMode} vehicleId={vehicleId} />
            </CameraViewContextProvider>
          </StyledForm>
        )}
      </Form>
    </Wrapper>
  );
};

const RenderForm = ({ isFullScreenMode, vehicleId }: { isFullScreenMode: boolean; vehicleId: number }) => {
  useUpdateFormAfterSync(vehicleId, { section: true, checkboxes: true, images: false });

  const { isOpened } = useCameraViewContext();
  if (isOpened) {
    return <CameraView />;
  }

  return (
    <>
      <QuestionsWrapper isFullScreenMode={isFullScreenMode}>
        {Object.keys(interiorFormValues.subsections).map(key => (
          <Level3Section key={key} sectionId={`interior_questions.subsections.${key}`} />
        ))}
      </QuestionsWrapper>

      <SubmitButton />
    </>
  );
};
